
"use client";

import React, { useMemo, useState } from "react";
import dynamic from 'next/dynamic';
import { format, isValid } from 'date-fns';
import { Trash2, PlusCircle, History, Wallet, Building, RotateCcw, BookOpen, Pencil, Briefcase } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { AddExpenseForm } from "@/components/add-expense-form";
import { formatCurrency } from "@/lib/utils";
import type { Expense } from "@/types/expense";
import { Timestamp } from "firebase/firestore";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { EditExpenseForm } from "@/components/edit-expense-form";

const CsvExport = dynamic(() => import('@/components/csv-export').then(mod => mod.CsvExport), { ssr: false });
const ConfirmationDialog = dynamic(() => import('@/components/confirmation-dialog').then(mod => mod.ConfirmationDialog), { ssr: false });


interface ExpenseCardProps {
  title: string;
  icon: React.ReactNode;
  expenses: Expense[];
  pastExpenses?: Expense[];
  allExpensesForExport?: Expense[];
  onAddExpense: (values: { amount: number, description: string, retailer?: string, employer?: string }) => void;
  onUpdateExpense: (id: string, values: { amount: number, description: string, retailer?: string, employer?: string, date: Date }) => void;
  onDeleteExpense: (id: string) => void;
  onClearAll?: () => void;
  isWorkCard?: boolean;
}

const formatDate = (date: any) => {
    if (!date) return 'No date';
    if (date instanceof Timestamp) {
        return format(date.toDate(), 'PP');
    }
    if (typeof date === 'string') {
        const d = new Date(date);
        if (isValid(d)) {
            return format(d, 'PP');
        }
    }
    if (date.seconds && typeof date.seconds === 'number') {
        return format(new Date(date.seconds * 1000), 'PP');
    }
    return 'Invalid date';
}

const sortExpenses = (expenses: Expense[]) => {
  return [...expenses].sort((a, b) => {
    const dateA = a.date instanceof Timestamp ? a.date.toMillis() : (a.date as any)?.seconds * 1000 || new Date(a.date as string).getTime();
    const dateB = b.date instanceof Timestamp ? b.date.toMillis() : (b.date as any)?.seconds * 1000 || new Date(b.date as string).getTime();
    return dateB - dateA;
  });
}


export default function ExpenseCard({
  title,
  icon,
  expenses,
  pastExpenses = [],
  allExpensesForExport,
  onAddExpense,
  onUpdateExpense,
  onDeleteExpense,
  onClearAll,
  isWorkCard = false,
}: ExpenseCardProps) {
  const [activeAccordion, setActiveAccordion] = useState<string | undefined>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState<{ title: string; description: string; onConfirm: () => void; } | null>(null);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);

  const totalExpenses = useMemo(() => {
    return expenses.reduce((acc, expense) => acc + expense.amount, 0);
  }, [expenses]);

  const sortedCurrentExpenses = useMemo(() => sortExpenses(expenses), [expenses]);
  const sortedPastExpenses = useMemo(() => sortExpenses(pastExpenses), [pastExpenses]);

  const handleDeleteClick = (expense: Expense) => {
    setDialogConfig({
      title: "Are you sure?",
      description: `This will permanently delete the expense: "${expense.description}".`,
      onConfirm: () => {
        onDeleteExpense(expense.id);
        setDialogOpen(false);
      }
    });
    setDialogOpen(true);
  };

  const handleEditClick = (expense: Expense) => {
    setEditingExpense(expense);
  };
  
  const handleClearAllClick = () => {
    if (onClearAll) {
      const dialogDetails = isWorkCard
        ? {
            title: "Reset Pay Period?",
            description: "This will clear all current work expenses from the view, marking the start of a new pay period. The records will be saved for CSV export.",
          }
        : {
            title: "Are you sure?",
            description: `This will clear all currently shown expenses for "${title}". This action cannot be undone.`,
          };
        
        setDialogConfig({
            ...dialogDetails,
            onConfirm: () => {
                onClearAll();
                setDialogOpen(false);
            }
        });
        setDialogOpen(true);
    }
  };

  const renderExpenseList = (list: Expense[]) => (
    <ScrollArea className="h-64 rounded-md border">
      <div className="p-4">
        {list.length > 0 ? (
          list.map((expense, index) => (
            <React.Fragment key={expense.id}>
              <div className="flex items-start justify-between py-2">
                  <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{expense.description}</p>
                        {isWorkCard && expense.employer && (
                          <Badge variant="outline" className="text-[10px] h-4 px-1 leading-none uppercase">
                            {expense.employer}
                          </Badge>
                        )}
                      </div>
                      {isWorkCard && expense.retailer && (
                        <div className="flex items-center text-sm text-muted-foreground">
                            <Building className="mr-1 h-3 w-3" />
                            <span>{expense.retailer}</span>
                        </div>
                      )}
                      <p className="text-sm text-muted-foreground">{formatDate(expense.date)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                      <span className="font-mono text-right mr-2">{formatCurrency(expense.amount)}</span>
                      <Button variant="ghost" size="icon" onClick={() => handleEditClick(expense)} className="text-muted-foreground hover:text-primary h-8 w-8" title="Edit Expense">
                          <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteClick(expense)} className="text-muted-foreground hover:text-destructive h-8 w-8" title="Delete Expense">
                          <Trash2 className="h-4 w-4" />
                      </Button>
                  </div>
              </div>
              {index < list.length - 1 && <Separator />}
            </React.Fragment>
          ))
        ) : (
          <p className="text-center text-muted-foreground py-8">No expenses recorded for this period.</p>
        )}
      </div>
    </ScrollArea>
  );

  return (
    <>
      <Card className="flex flex-col h-full backdrop-blur border-white/10 bg-card/5">
        <CardHeader className="flex-row items-center gap-4 space-y-0">
          {icon}
          <div className="flex-1">
            <CardTitle>{title}</CardTitle>
            <CardDescription>{isWorkCard ? "Current period" : "Total spent"}</CardDescription>
          </div>
          <div className="text-3xl font-bold text-primary">{formatCurrency(totalExpenses)}</div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col gap-4">
          <Accordion type="single" collapsible value={activeAccordion} onValueChange={setActiveAccordion}>
            <AccordionItem value="add-expense" className="border-b-0">
              <AccordionTrigger className="bg-secondary hover:bg-muted rounded-md px-4 py-2 text-sm font-semibold [&[data-state=open]>svg]:text-primary">
                <div className="flex items-center gap-2">
                  <PlusCircle className="h-5 w-5" />
                  Add New Expense
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <AddExpenseForm
                  onAddExpense={onAddExpense}
                  onDone={() => setActiveAccordion(undefined)}
                  isWorkCard={isWorkCard}
                />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="view-history" className="border-b-0 mt-2">
              <AccordionTrigger className="bg-secondary hover:bg-muted rounded-md px-4 py-2 text-sm font-semibold [&[data-state=open]>svg]:text-primary" disabled={expenses.length === 0}>
                  <div className="flex items-center gap-2">
                      <History className="h-5 w-5" />
                      View Current Expenses
                  </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                {renderExpenseList(sortedCurrentExpenses)}
              </AccordionContent>
            </AccordionItem>
            {isWorkCard && (
                <AccordionItem value="view-past-expenses" className="border-b-0 mt-2">
                    <AccordionTrigger className="bg-secondary hover:bg-muted rounded-md px-4 py-2 text-sm font-semibold [&[data-state=open]>svg]:text-primary" disabled={pastExpenses.length === 0}>
                        <div className="flex items-center gap-2">
                            <BookOpen className="h-5 w-5" />
                            View Past Expenses
                        </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                        {renderExpenseList(sortedPastExpenses)}
                    </AccordionContent>
                </AccordionItem>
            )}
          </Accordion>
        </CardContent>
        <CardFooter className="flex-col gap-4">
          {isWorkCard && (
            <CsvExport expenses={allExpensesForExport || []} />
          )}
          {onClearAll && (
             <Button onClick={handleClearAllClick} className="w-full" disabled={expenses.length === 0}>
              {isWorkCard ? (
                <>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reset Pay Period
                </>
              ) : (
                <>
                  <Wallet className="mr-2 h-4 w-4" />
                  Clear All
                </>
              )}
            </Button>
          )}
        </CardFooter>
      </Card>
      
      {dialogOpen && dialogConfig && (
        <ConfirmationDialog 
            isOpen={dialogOpen}
            onOpenChange={setDialogOpen}
            title={dialogConfig.title}
            description={dialogConfig.description}
            onConfirm={dialogConfig.onConfirm}
            onCancel={() => setDialogOpen(false)}
        />
      )}

      <Dialog open={!!editingExpense} onOpenChange={(open) => !open && setEditingExpense(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Expense</DialogTitle>
          </DialogHeader>
          {editingExpense && (
            <EditExpenseForm
              expense={editingExpense}
              onUpdateExpense={(values) => {
                onUpdateExpense(editingExpense.id, values);
                setEditingExpense(null);
              }}
              onDone={() => setEditingExpense(null)}
              isWorkCard={isWorkCard}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
